/**
 * Deciding whether an arriving `?ceremony=<unitId>` should actually open the
 * selo ceremony.
 *
 * The class back cover fires this param on the strength of what the student saw
 * ("this is the unit's last class"), which is not the same as "the unit is now
 * complete" — earlier classes can still be outstanding. The journey wall is the
 * authoritative record, so the decision is re-made here against wall data and
 * the param is treated as a request, not an instruction.
 */

export interface CeremonyCandidate {
  unitId: string;
  status: string;
  badgeClaimedAt?: string | Date | null;
}

/**
 * Returns the unit whose ceremony should open, or null to ignore the request.
 *
 * Null when: no param, the unit isn't on this wall, or the unit isn't actually
 * completed yet — a "you earned the cartaz" ceremony for an unfinished unit
 * would be a lie.
 *
 * An already-claimed selo does NOT block the ceremony: `?ceremony=` always
 * replays so the flow stays reproducible from a URL. The trade-off is that a
 * refresh or back-navigation while the param is still on the URL replays it;
 * the journey page strips the param immediately after opening to limit that.
 */
export function resolveCeremonyUnit<T extends CeremonyCandidate>(
  units: readonly T[] | undefined,
  ceremonyUnitId: string | undefined,
): T | null {
  if (!ceremonyUnitId || !units) return null;
  const unit = units.find((u) => u.unitId === ceremonyUnitId);
  if (!unit) return null;
  if (unit.status !== 'completed') return null;
  return unit;
}

/**
 * Whether the wall data is trustworthy enough to decide on a `?ceremony=`
 * request. Arriving from a class's back cover, the wall query usually still
 * holds the copy cached BEFORE that class was completed (the unit reads
 * "in progress"); the mutation invalidated it, so a refetch is already in
 * flight. Deciding on the stale copy drops the request for good, and the
 * ceremony only plays "the second time around". Wait for the refetch (and
 * for a real result, not the previous journey's placeholder) instead.
 */
export function isCeremonyWallSettled(opts: {
  hasData: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
}): boolean {
  return opts.hasData && !opts.isFetching && !opts.isPlaceholderData;
}
